import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { useToasts } from '@air/provider-toast';
import { useSysadmin } from '@air/sysadmin';

import { useWorkspacesSwitchWorkspacseMutation } from '~/components/v2/hooks/queries/mutations/useWorkspacesSwitchWorkspacseMutation';
import { Pathname } from '~/constants/pathname';

/**
 * Paths that set the current workspace should be forcefully redirected to the search page.
 */
const FORCED_REDIRECT_PATHNAMES: string[] = [Pathname.BoardByBoardId, Pathname.SavedFilter, Pathname.SavedFilter];

/**
 * This hook switches current workspace based on the workspace query parameter "wsid".
 */
export const useWorkspaceQueryParam = (enabled: boolean) => {
  const router = useRouter();
  const { showToast } = useToasts();
  const { mutate: switchWorkspaceMutation } = useWorkspacesSwitchWorkspacseMutation();
  const switchedRef = useRef(false);
  const { isInternal, handleSetWorkspaceId } = useSysadmin();

  useEffect(() => {
    const wsid = typeof router.query.wsid === 'string' ? router.query.wsid : undefined;

    if (!switchedRef.current && wsid && enabled) {
      switchedRef.current = true;

      if (FORCED_REDIRECT_PATHNAMES.includes(router.pathname)) {
        router.replace(Pathname.Search, undefined, { shallow: false });
      } else {
        const newQuery = { ...router.query };

        /**
         * If we don't do this, the query param will still be present in the URL but value would be empty.
         * @example `https://localhost:3000/all?wsid=123` -> `https://localhost:3000/all?wsid=`
         */
        delete newQuery.wsid;

        router.replace(
          {
            pathname: router.pathname,
            query: newQuery,
          },
          undefined,
          { shallow: false },
        );
      }

      if (isInternal) {
        handleSetWorkspaceId(wsid);
      } else {
        switchWorkspaceMutation(
          {
            workspaceId: wsid,
          },
          {
            onSuccess: () => {
              showToast('Workspace switched');
            },
            onError: () => {
              showToast('Failed to switch workspace', {
                color: 'red',
              });
            },
          },
        );
      }
    }
  }, [handleSetWorkspaceId, isInternal, showToast, switchWorkspaceMutation, enabled, router]);
};
